import _ from "lodash";
import { Map } from "immutable";
import { createSelector } from "reselect";
import { Action } from "typescript-fsa";

import { ApiState as State } from "../models/state";
import { ApiRequest } from "../requests";

export interface PropsWithUuid {
    params: {
        uuid: string;
    };
}

export interface PropsWithUuids {
    params: {
        uuids: string[];
    };
}

export interface ProspWithActionCreator {
    actionCreator: ActionCreator;
}

export interface ProspWithActionCreators {
    actionCreators: ActionCreator[];
}

type ActionCreator = () => Action<ApiRequest>;

export const getAllRequests = (state: State) => state.api.requests;
export const getUriToRequests = (state: State) => state.api.uriToRequests;

const getRequest = (state: State, props: PropsWithUuid) =>
    getAllRequests(state).get(props.params.uuid);

const getRequests = (state: State, props: PropsWithUuids) => {
    const requests = Array<ApiRequest>(props.params.uuids.length);
    const allRequests = getAllRequests(state);
    for (let i = 0; i < props.params.uuids.length; i++) {
        requests[i] = allRequests.get(props.params.uuids[i]);
    }
    return requests;
};

export const makeGetRequest = () => createSelector(
    getRequest,
    (request: ApiRequest) => request,
);

export const makeGetRequests = () => createSelector(
    getRequests,
    (requests: ApiRequest[]) => requests,
);

const getEndpointFromActionCreator = (actionCreator: ActionCreator) => {
    const action = actionCreator();
    return action.payload.getEndpoint();
};

const getLastRequest = (state: State, props: ProspWithActionCreator) => {
    const endpoint = getEndpointFromActionCreator(props.actionCreator);
    const uriToRequests = getUriToRequests(state);
    if (uriToRequests.has(endpoint)) {
        const reqs = uriToRequests.get(endpoint);
        return getRequest(state, {
            params: {
                uuid: reqs[reqs.length - 1],
            },
        });
    }
    return null;
};

const getLastRequests = (state: State, props: ProspWithActionCreators) => _.map(
    props.actionCreators,
    (actionCreator) => getLastRequest(state, { actionCreator }),
);

export const makeGetLastRequest = () => createSelector(
    getLastRequest,
    (request: ApiRequest) => request,
);

export const makeGetLastRequests = () => createSelector(
    getLastRequests,
    (requests: ApiRequest[]) => requests,
);

export const makeGetRequestLastLoaded = () => createSelector(
    getLastRequest,
    (request: ApiRequest) => request != null ? request.finished : null,
);

export const makeGetRequestsLastLoaded = () => createSelector(
    getLastRequests,
    (requests: ApiRequest[]) => _.map(
        requests, (request) => request != null ? request.finished : null,
    ),
);

// Gets all requests that have had the progress attribute set..
export const getRequestsInProgress = createSelector(
    getAllRequests,
    (requests: Map<string, ApiRequest>) => requests.filter((req) => {
        return req.progress != null;
    }).toArray(),
);
